import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { concatLatestFrom } from '@ngrx/operators';
import isEqual from 'fast-deep-equal';
import uniq from 'lodash-es/uniq';
import {
  BehaviorSubject,
  combineLatest,
  combineLatestWith,
  distinctUntilChanged,
  filter,
  first,
  firstValueFrom,
  map,
  Observable,
  of,
  startWith,
  Subject,
  tap,
} from 'rxjs';

import { runtimeActions, runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  ColumnId,
  getFormGroupLikeWrapper,
  PropsOf,
  SlotId,
  SlotIdOf,
} from '@blaze-runtime/engine-domain';
import {
  DEFAULT_ELEMENT_PADDING,
  ElementHorizontalAlignment,
  getActiveBinding,
  getBackground,
  makeBorderStyles,
  makeContainerVisualEffectStyles,
  RenderElementContext,
  RuntimeContext,
} from '@blaze-shared/elements-domain';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { type ElementSchema } from '@blaze-shared/schema-domain';
import { isDefined, setAlphaToHexColor, useEmitTracker } from '@blaze-shared/shared-util';

import { getElementEntities } from '../../+state/elements/elements.selectors';
import { CanvasElementComponent, ComponentSlotData } from '../../canvas-elements/element-component';
import { getWrapperRefs } from '../../canvas-elements/element-model/wrapper-refs';
import { FormGroupLikeRenderElementInstance } from '../../canvas-elements/models/render-element.models';
import { FormGroupUtilsService } from '../../services/form-group-utils.service';
import { ProjectNavigateService } from '../../services/project-navigate.service';
import { isElementChildOfAtSlot } from '../../utils/layout/relationship';
import { CanvasElementBaseComponent } from '../__base/base.component';

import { selectWizardStepFormOutputs } from './wizard-step-form-outputs';
import { WizardProgressPercentProps, WizardStep } from './wizard.models';
import {
  getWizardConfirmationPostAction,
  getWizardConfirmationSlotId,
  getWizardConfirmationSlotIndex,
  getWizardProgressPercent,
  getWizardSteps,
  isQuestionlessQuestionnaire,
  isWizardReviewSlot,
} from './wizard.utils';

type WizardStepUI = WizardStep & { index: number; show?: boolean };
type WizardConfirmation = {
  title: string;
  message: string;
  buttonLabel?: string;
  page?: string;
  image?: string;
};

@UntilDestroy()
@Component({
  selector: 'be-wizard',
  templateUrl: './wizard.component.html',
  styleUrls: ['./wizard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WizardComponent
  extends CanvasElementBaseComponent<'wizard'>
  implements CanvasElementComponent<'wizard'>, OnChanges, OnInit, FormGroupLikeRenderElementInstance
{
  private readonly formGroupUtilsService = inject(FormGroupUtilsService);
  private readonly notificationService = inject(NotificationService);
  private readonly projectNavigateService = inject(ProjectNavigateService);

  @Input() value!: Record<string, any>;
  @Input() slotsProps?: Record<SlotIdOf<'wizard'>, PropsOf<'wizard'>>;

  @HostBinding('style.--b-sc-internal-padding') internalPadding = DEFAULT_ELEMENT_PADDING;
  @HostBinding('style.--b-sc-button-primary-text') buttonPrimaryTextColor = '#fff';
  @HostBinding('style.--b-sc-button-primary-fill') buttonPrimaryFill = '#00f';
  @HostBinding('style.--b-sc-button-primary-fill-color') buttonPrimaryFillColor = '#00f';
  @HostBinding('style.--b-sc-button-primary-outline') buttonPrimaryOutlineColor = '#00f';
  @HostBinding('style.--b-sc-button-secondary-text') buttonSecondaryTextColor = '#000';
  @HostBinding('style.--b-sc-button-secondary-fill') buttonSecondaryFill = '#fff';
  @HostBinding('style.--b-sc-button-secondary-outline') buttonSecondaryOutlineColor = '#eee';
  @HostBinding('style.--b-sc-button-secondary-outline-faint') buttonSecondaryOutlineFaintColor =
    '#eee';
  @HostBinding('style.--b-sc-container-fill') containerFill = '#fff';
  @HostBinding('style.--b-sc-container-border-radius') containerBorderRadius = '0';

  private readonly outputEmitTracker = useEmitTracker();
  private readonly dataValue = this.getDataInput('value');
  private readonly statusEmitTracker = useEmitTracker();
  private statusSchema?: ElementSchema;

  // Stepper presentation, derived from content.layout in updateStyles() - template only
  protected stepperStyle = '';
  protected placement = '';
  protected align: ElementHorizontalAlignment = 'justify';
  protected showStepNumbers = true;
  protected progressPercent: Required<WizardProgressPercentProps> = {
    enabled: false,
    position: 'right',
  };

  override slots?: ComponentSlotData<'wizard', PropsOf<'wizard'>>;

  selectedSlotId$$: BehaviorSubject<ColumnId | undefined> = new BehaviorSubject<
    ColumnId | undefined
  >(undefined);
  selectedSlotIndex$$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  absoluteSelectedSlotIndex$$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  steps$$: BehaviorSubject<WizardStepUI[]> = new BehaviorSubject<WizardStepUI[]>([]);
  confirmation$$: BehaviorSubject<WizardConfirmation | undefined> = new BehaviorSubject<
    WizardConfirmation | undefined
  >(undefined);
  emptySlotMessage$: Observable<string | undefined>;
  noSlotsMessage$: Observable<string | undefined>;
  updateConfirmation$$: Subject<void> = new Subject();
  schema: ElementSchema | undefined;

  labelEnabled!: boolean;
  labelText!: string;
  contentStyles = signal<Record<string, string> | null>(null);
  wrapperStyles: Record<string, string> | null = null;

  /**
   * What the canvas shows in place of a questionnaire that has no questions yet: its only
   * slot is the confirmation's, so without this the builder is handed the post-submit
   * "Thank you!" screen on a wizard nobody has filled in.
   *
   * Editor-only. A published questionnaire with no questions is one its builder never
   * finished, and pointing its end-user at the properties panel would be worse than the
   * empty box they get instead.
   */
  get emptyQuestionnaireHint(): string | undefined {
    return this.isInEditor && isQuestionlessQuestionnaire(this.props)
      ? 'Add a Question from the properties panel first.'
      : undefined;
  }

  get slotElementContext(): { elementContext: RenderElementContext } {
    return {
      elementContext: {
        disableShadowEffect: true,
        dataContainer: 'wizard',
      },
    };
  }

  constructor() {
    super();

    this.emptySlotMessage$ = combineLatest({
      element: this.element$,
      absoluteSelectedSlotIndex: this.absoluteSelectedSlotIndex$$,
      selectedSlotId: this.selectedSlotId$$,
    }).pipe(
      map(({ element, absoluteSelectedSlotIndex, selectedSlotId }) =>
        element.slots[selectedSlotId ?? element.slots.ids[0]]?.children.length !== 0
          ? undefined
          : !getActiveBinding(element.props.data)
            ? 'Choose your primary table in the property panel.\nOptionally, add different components here.'
            : isWizardReviewSlot(element.props, absoluteSelectedSlotIndex)
              ? 'Add review components from Step properties,\nor drag other components here.'
              : 'Add related table from the plus icon,\nor drag other components here.',
      ),
    );
    this.noSlotsMessage$ = of('Add a Step from properties panel first.');

    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.slots) {
      const selectedSlotId = this.selectedSlotId$$.value;
      if (!this.slots?.slotIds.includes(selectedSlotId ?? '-1')) {
        this.selectedSlotId$$.next(this.slots?.slotIds[0]);
      } else {
        const selectedIndex = this.slots?.slotIds.indexOf(selectedSlotId ?? '-1') ?? -1;
        this.absoluteSelectedSlotIndex$$.next(selectedIndex >= 0 ? selectedIndex : 0);
      }
    }
    if (changes.props) {
      const title = this.props?.content.title;
      this.labelEnabled = title?.enabled ?? true;
      this.labelText = title?.label ?? '';
      this.updateStyles();
      this.updateConfirmation$$.next();
    }

    // NOTE model is bound and the input has changed -> propagate value
    if (changes['value'] && this.bindingResults?.['value']) {
      this.updateOutput();
    }
  }
  ngOnInit(): void {
    if (!this.bindingResults?.['value']) {
      this.setDefaultEventSchema('change');
    }
    this.setDefaultEventSchema('next');
    this.setDefaultEventSchema('submit');
    this.dataValue.schema$.pipe(filter(Boolean), untilDestroyed(this)).subscribe((schema) => {
      if (schema) {
        this.schema = this.createElementSchema('change', undefined, schema);
        this.setDefaultEventSchema('change', this.schema);
      }
    });

    // reset state after navigation
    this.store.dispatch(
      runtimeActions.updateElementMeta({
        elementId: this.elementId,
        meta: {
          dataFeatures: {
            elementType: 'wizard',
            selectedSlotId: undefined,
            filledSteps: undefined,
            hiddenSteps: undefined,
          },
        },
      }),
    );

    this.store
      .select(runtimeSelectors.selectElementMetaDataFeaturesById(this.elementId))
      .pipe(
        map((elementMeta) =>
          elementMeta && elementMeta.elementType === 'wizard'
            ? elementMeta.selectedSlotId
            : undefined,
        ),
        map((selectedSlotId) => selectedSlotId as ColumnId | undefined),
        untilDestroyed(this),
      )
      .subscribe(this.selectedSlotId$$);

    this.selectedSlotId$$
      .pipe(
        map((selectedSlotId) => {
          const selectedIndex = this.slots?.slotIds.indexOf(selectedSlotId as ColumnId) ?? -1;
          return selectedIndex >= 0 ? selectedIndex : 0;
        }),
        untilDestroyed(this),
      )
      .subscribe(this.absoluteSelectedSlotIndex$$);

    this.absoluteSelectedSlotIndex$$
      .pipe(
        map((selectedIndex) => {
          const confirmationSlotIndex = getWizardConfirmationSlotIndex(this.props);
          if (confirmationSlotIndex !== undefined && selectedIndex === confirmationSlotIndex) {
            return this.steps$$.value.length;
          }
          const uiIndex = this.steps$$.value.findIndex((step) => step.index === selectedIndex);
          return uiIndex < 0 ? 0 : uiIndex;
        }),
        untilDestroyed(this),
      )
      .subscribe(this.selectedSlotIndex$$);

    this.absoluteSelectedSlotIndex$$
      .pipe(
        combineLatestWith(this.updateConfirmation$$.pipe(startWith(undefined))),
        map(([selectedIndex]) => {
          const confirmationSlotIndex = getWizardConfirmationSlotIndex(this.props);
          const confirmationPostAction = getWizardConfirmationPostAction(this.props);
          return confirmationSlotIndex !== undefined && selectedIndex === confirmationSlotIndex
            ? {
                title: confirmationPostAction?.title ?? '',
                message: confirmationPostAction?.message ?? '',
                buttonLabel:
                  confirmationPostAction?.actionType === 'Button'
                    ? confirmationPostAction.buttonLabel
                    : undefined,
                page: confirmationPostAction?.page,
                image: confirmationPostAction?.image,
              }
            : undefined;
        }),
        untilDestroyed(this),
      )
      .subscribe(this.confirmation$$);

    this.element$
      .pipe(
        map((element) =>
          getFormGroupLikeWrapper(getWrapperRefs(element)).map(({ formId }) => formId),
        ),
        distinctUntilChanged(isEqual),
        untilDestroyed(this),
      )
      .subscribe((formIds: string[]) => {
        this.statusSchema = this.createElementSchema(
          'status',
          formIds.map((formId) => ({
            columnType: 'Boolean',
            dataType: 'Boolean',
            isProfilePicture: false,
            name: formId,
          })),
        );
        this.setDefaultEventSchema('status', this.statusSchema);
      });

    combineLatest({
      filledSteps: this.store
        .select(runtimeSelectors.selectElementMetaDataFeaturesById(this.elementId))
        .pipe(
          map((elementMeta) =>
            elementMeta && elementMeta.elementType === 'wizard'
              ? elementMeta.filledSteps
              : undefined,
          ),
          map((filledSteps) => filledSteps as ColumnId[] | undefined),
          filter(isDefined),
          startWith([]),
        ),
      element: this.element$,
    })
      .pipe(
        concatLatestFrom(() => this.store.select(getElementEntities)),
        untilDestroyed(this),
      )
      .subscribe(([{ filledSteps, element }, elements]) => {
        const formIds = getFormGroupLikeWrapper(getWrapperRefs(element)).map(
          ({ formId }) => formId,
        );
        const status = Object.fromEntries(
          formIds.map((formId) => [
            formId,
            filledSteps.some((slotId) =>
              isElementChildOfAtSlot(elements, formId, element.id, slotId),
            ),
          ]),
        );
        if (this.statusEmitTracker.shouldEmit(status)) {
          setTimeout(() => {
            this.output.emit({
              eventType: 'status',
              data: status,
              schema: this.statusSchema,
              isInitialData: false,
            });
          });
        }
      });

    combineLatest({
      props: this.props$,
      context: this.runtimeContext$,
      formsData: selectWizardStepFormOutputs(this.store, this.elementId),
    })
      .pipe(
        map(({ props, context, formsData }) =>
          getWizardSteps(props).map((step, index) => ({
            ...step,
            index,
            show: this.showWizardStep(step, context, formsData),
          })),
        ),
        tap((steps) => {
          this.store.dispatch(
            runtimeActions.updateElementMeta({
              elementId: this.elementId,
              meta: {
                dataFeatures: {
                  elementType: 'wizard',
                  hiddenSteps: steps?.filter(({ show }) => !show).map(({ index }) => index),
                },
              },
            }),
          );
          if (!this.isInEditor && steps?.[this.absoluteSelectedSlotIndex$$.value]?.show === false) {
            const firstVisible = steps
              .slice(this.absoluteSelectedSlotIndex$$.value)
              .find(({ show }) => show);
            const slotId = firstVisible && this.slots?.slotIds[firstVisible.index];
            if (slotId !== undefined) {
              this.store.dispatch(
                runtimeActions.updateElementMeta({
                  elementId: this.elementId,
                  meta: {
                    dataFeatures: {
                      elementType: 'wizard',
                      selectedSlotId: slotId,
                    },
                  },
                }),
              );
            }
          }
        }),
        map(
          (steps) =>
            steps?.filter(
              (step) => step.type !== 'confirmation' && (this.isInEditor || step.show),
            ) ?? [],
        ),
        untilDestroyed(this),
      )
      .subscribe(this.steps$$);

    this.loadDraft();
  }

  selectStep(index: number): void {
    if (this.selectedSlotIndex$$.value === undefined) {
      return;
    }
    if (this.isInEditor || index < this.selectedSlotIndex$$.value + 1) {
      this.navigate(index);
    } else if (index === this.selectedSlotIndex$$.value + 1) {
      this.next();
    }
  }

  submit(): void {
    if (this.absoluteSelectedSlotIndex$$.value === undefined) {
      return;
    }
    const slotId = getWizardConfirmationSlotId(this.props, this.slots?.slotIds);

    // Emit this after the current event loop to ensure the statuses of the forms are updated
    setTimeout(() => {
      this.output.emit({
        eventType: 'submit',
        data: slotId,
        schema: this.getDefaultEventSchema('submit'),
        output: null,
        isInitialData: false,
      });
    });
  }

  next(): void {
    if (
      this.selectedSlotIndex$$.value === undefined ||
      this.selectedSlotIndex$$.value >= this.steps$$.value.length - 1
    ) {
      return;
    }
    const currentStepIdx = this.steps$$.value[this.selectedSlotIndex$$.value].index;
    const stepIdx = this.steps$$.value[this.selectedSlotIndex$$.value + 1].index;
    const slotId = this.slots?.slotIds[stepIdx];
    this.output.emit({
      eventType: 'next',
      data: { slotId, currentStepIdx },
      schema: this.getDefaultEventSchema('next'),
      output: null,
      isInitialData: false,
    });
  }

  back(): void {
    if (this.selectedSlotIndex$$.value === undefined || this.selectedSlotIndex$$.value === 0) {
      return;
    }
    this.navigate(this.selectedSlotIndex$$.value - 1);
  }

  redirect(): void {
    if (this.confirmation$$.value?.page) {
      this.projectNavigateService.navigateToPage({ targetPageId: this.confirmation$$.value?.page });
    }
  }

  canSaveDraft(): boolean {
    return this.props?.content.action === 'add' && this.props?.content.saveDraftEnabled !== false;
  }

  saveDraft(): void {
    this.formGroupUtilsService.saveDraft(this.element$, this.elementId).subscribe({
      complete: () => {
        this.notificationService.success('Draft saved', true);
      },
      error: () => {
        this.notificationService.error('Unable to save draft', true);
      },
    });
  }

  loadDraft(): void {
    if (!this.canSaveDraft()) {
      return;
    }
    this.formGroupUtilsService.loadDraft(this.element$, this.elementId).subscribe();
  }

  validateValue(): void {
    // nothing to do
  }

  valueUpdated(): void {
    // nothing to do
  }

  getInstanceValue(): Record<string, any> {
    return this.value;
  }

  shouldEmitOutput(): boolean {
    return this.outputEmitTracker.hasChanges();
  }

  async showSlot(slotId?: SlotId): Promise<void> {
    if (!slotId) {
      return;
    }

    const stepIndex = this.slots?.slotIds.indexOf(`${slotId}` as any);
    if (!isDefined(stepIndex) || stepIndex === -1) {
      return;
    }

    this.navigate(stepIndex);
    await firstValueFrom(this.selectedSlotIndex$$.pipe(filter((id) => id === stepIndex)));
  }

  protected getProgressPercent(selectedIndex: number, stepsCount: number): number {
    return getWizardProgressPercent(selectedIndex, stepsCount);
  }

  /** Step label as the stepper shows it - numbered unless the builder turned numbering off. */
  protected numberedStepLabel(index: number, label: string): string {
    return this.showStepNumbers ? `${index + 1}. ${label}` : label;
  }

  /** Same label for the single-line "current step" header; empty past the last step. */
  protected numberedSelectedStepLabel(selectedIndex: number, steps: WizardStepUI[]): string {
    return selectedIndex < steps.length
      ? this.numberedStepLabel(selectedIndex, steps[selectedIndex].label)
      : '';
  }

  private navigate(stepIndex: number): void {
    combineLatest({
      element: this.element$,
      elements: this.store.select(getElementEntities),
      selectedSlotId: this.selectedSlotId$$,
      formsState: this.store.select(runtimeSelectors.selectFormsState),
      filledSteps: this.store
        .select(runtimeSelectors.selectElementMetaDataFeaturesById(this.elementId))
        .pipe(
          map((elementMeta) =>
            elementMeta && elementMeta.elementType === 'wizard'
              ? elementMeta.filledSteps
              : undefined,
          ),
          map((filledSteps) => filledSteps as ColumnId[] | undefined),
        ),
    })
      .pipe(
        first(),
        map(({ element, elements, selectedSlotId, formsState, filledSteps }) => {
          const slotId = selectedSlotId ?? element.slots.ids[0];
          const formIds = getFormGroupLikeWrapper(getWrapperRefs(element)).map(
            ({ formId }) => formId,
          );
          const currentStepForms = formIds.filter((formId) =>
            isElementChildOfAtSlot(elements, formId, element.id, slotId),
          );
          const anyFormFilled = currentStepForms.some((formId) => !formsState[formId]?.blank);
          return anyFormFilled
            ? uniq([...(filledSteps ?? []), slotId])
            : (filledSteps ?? []).filter((step) => step !== slotId);
        }),
      )
      .subscribe((filledSteps) => {
        const slotId = this.slots?.slotIds[this.steps$$.value[stepIndex].index];
        this.store.dispatch(
          runtimeActions.updateElementMeta({
            elementId: this.elementId,
            meta: {
              dataFeatures: {
                elementType: 'wizard',
                selectedSlotId: slotId,
                filledSteps,
              },
            },
          }),
        );
      });
  }

  private showWizardStep(
    step: WizardStep,
    context: RuntimeContext,
    formsData: Record<string, unknown>,
  ): boolean {
    if (step.required) {
      return true;
    }
    if (
      step.visible?.accessRoles?.length &&
      !this.userAccessRolesService.isCurrentUserInAccessRoles(step.visible.accessRoles)
    ) {
      return false;
    }
    return this.evaluateExpression(step.visibilityExpression, { ...context, ...formsData }) ?? true;
  }

  private updateOutput(): void {
    if (this.outputEmitTracker.shouldEmit(this.value)) {
      this.output.emit({
        eventType: 'change',
        data: this.value,
        schema: this.schema,
        isInitialData: false,
      });
    }
  }

  private updateStyles(): void {
    this.stepperStyle = this.props?.content.layout?.style ?? this.stepperStyle;
    this.showStepNumbers =
      (this.stepperStyle !== 'tabs' && this.stepperStyle !== 'lines') ||
      (this.props?.content.layout?.numerateTabs ?? true);
    const progressPercent = this.props?.content.layout?.progressPercent;
    this.progressPercent = {
      enabled: progressPercent?.enabled ?? false,
      position: progressPercent?.position ?? 'right',
    };
    this.placement = this.props?.content.layout?.placement ?? this.placement;
    this.align = this.props?.content.layout?.align.horizontal ?? this.align;

    const style = this.props?.style;
    this.buttonPrimaryTextColor =
      style?.buttons?.primary.text.color1 ?? this.buttonPrimaryTextColor;
    this.buttonPrimaryFill = style?.buttons?.primary.fill
      ? getBackground(style?.buttons?.primary.fill)
      : this.buttonPrimaryFill;
    this.buttonPrimaryFillColor =
      style?.buttons?.primary.fill.color1 ?? this.buttonPrimaryFillColor;
    this.buttonPrimaryOutlineColor =
      style?.buttons?.primary.outline.selectorType === 'none'
        ? 'transparent'
        : (style?.buttons?.primary.outline.color1 ?? this.buttonPrimaryOutlineColor);
    this.buttonSecondaryTextColor =
      style?.buttons?.secondary.text.color1 ?? this.buttonSecondaryTextColor;
    this.buttonSecondaryFill = style?.buttons?.secondary.fill
      ? getBackground(style?.buttons?.secondary.fill)
      : this.buttonSecondaryFill;
    this.buttonSecondaryOutlineColor =
      style?.buttons?.secondary.outline.selectorType === 'none'
        ? 'transparent'
        : (style?.buttons?.secondary.outline.color1 ?? this.buttonSecondaryOutlineColor);
    this.buttonSecondaryOutlineFaintColor =
      style?.buttons?.secondary.outline.selectorType === 'none'
        ? 'transparent'
        : style?.buttons?.secondary.outline.color1
          ? setAlphaToHexColor(style?.buttons?.secondary.outline.color1, 50)
          : this.buttonSecondaryOutlineFaintColor;
    this.containerFill = style?.container?.fill
      ? getBackground(style?.container?.fill)
      : this.containerFill;
    this.containerBorderRadius = style?.container?.radius ?? this.containerBorderRadius;
    this.internalPadding = style?.general?.offset.padding ?? DEFAULT_ELEMENT_PADDING;
    this.contentStyles.set(
      makeBorderStyles(style?.container?.outline, style?.container?.outlineWidth),
    );
    this.wrapperStyles = {
      ...makeContainerVisualEffectStyles(style?.container?.visualEffect),
      ...makeBorderStyles(style?.general?.outline, style?.general?.outlineWidth),
    };
  }
}
